import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { selectUserApproval } from "pages/EmailApproval/slice/selector";
import { useEmailApprovalSlice } from "../slice";

import {
  ApprovalInformationSection,
  CommonWrapper,
  MarketplaceApprovalStyles
} from "pages/Marketplace/CampaignApproval/styled";

import LoadingSpinner from "components/PageLoader/Spinner";
import Button from "components/Buttons";

const getApprovalPayload = (search) => {
  const query = new URLSearchParams(search);

  let params = query.get("params");
  const id_hash = query.get("id_hash");

  // FIX: remove extra quotes from URL param
  if (params) {
    params = params.replace(/^"+|"+$/g, "");
    return { params };
  }

  if (id_hash) return { id_hash };

  return null;
};

const UserApproval = () => {
  const dispatch = useDispatch();
  const { search } = useLocation();
  const { actions } = useEmailApprovalSlice();

  const { loading, data } = useSelector(selectUserApproval);

  // payload from URL
  const payload = useMemo(() => getApprovalPayload(search), [search]);

  // =========================
  // FETCH USER DETAILS
  // =========================
  useEffect(() => {
    if (payload) {
      dispatch(actions.fetchUserApproval(payload));
    }
  }, [payload, dispatch, actions]);

  const user = data?.user;
  const inviter = user?.inviter_details;

  // backend id (fallback safe)
  const userId = user?.id_hash || payload?.params;

  // =========================
  // APPROVE USER
  // =========================
  const handleApprove = () => {
    if (!payload?.params) return;

    dispatch(
      actions.activateUserRequest({
        params: payload.params
      })
    );
  };

  // =========================
  // BLOCK USER
  // =========================
  const handleBlockUser = () => {
    if (!payload?.params) return;
    if (!userId) return;

    dispatch(
      actions.disableUserRequest({
        params: payload.params
        // user_id_hash: userId
      })
    );
  };

  const handleDomainBlockUser = () => {
    if (!payload?.params) return;
    if (!userId) return;

    dispatch(
      actions.disableUserRequest({
        params: payload.params,
        block_domain: "yes"
      })
    );
  };

  return (
    <CommonWrapper className={loading ? "common-loading" : "common-styles"}>
      {loading ? (
        <div className="loading-state">
          <LoadingSpinner color="#A049FC" width="20" height="20" />
          <span>Loading...</span>
        </div>
      ) : (
        <>
          <MarketplaceApprovalStyles>
            <div className="info-header">
              <h2>Brand User - Signup Approval</h2>
            </div>

            <div className="approval-information-container user-approval">
              <div>
                {/* USER INFO */}
                <ApprovalInformationSection>
                  <h3 className="section-title">User Information</h3>

                  <ul className="approval-details-grid">
                    <li>
                      <span>Name</span>
                      <p>
                        {user?.first_name} {user?.last_name}
                      </p>
                    </li>

                    <li>
                      <span>Email</span>
                      <p>{user?.email || "-"}</p>
                    </li>

                    <li>
                      <span>Phone Number</span>
                      <p>{user?.phone_number || "-"}</p>
                    </li>

                    <li>
                      <span>Domain</span>
                      <p>{user?.domain || "-"}</p>
                    </li>
                  </ul>
                </ApprovalInformationSection>

                {/* INVITER INFO */}
                {inviter && (
                  <ApprovalInformationSection>
                    <h3 className="section-title">Invite User Details</h3>

                    <ul className="approval-details-grid">
                      <li>
                        <span>Inviter Email</span>
                        <p>{inviter?.inviter_email || "-"}</p>
                      </li>

                      <li>
                        <span>Total Invited</span>
                        <p>{inviter?.total_invited_user_count || "-"}</p>
                      </li>

                      <li>
                        <span>Pending</span>
                        <p>{inviter?.pending_count || "-"}</p>
                      </li>

                      <li>
                        <span>Approved</span>
                        <p>{inviter?.approved_count || "-"}</p>
                      </li>

                      <li>
                        <span>Rejected</span>
                        <p>{inviter?.rejected_count || "-"}</p>
                      </li>
                    </ul>
                  </ApprovalInformationSection>
                )}
              </div>
            </div>
          </MarketplaceApprovalStyles>

          {/* ACTIONS */}
          <ApprovalInformationSection className="user-approval">
            {user?.status === "activated" ? (
              <Button colorScheme="success" type="button" disabled>
                Activated
              </Button>
            ) : user?.status === "deactivated" ? (
              <Button colorScheme="error" type="button" disabled>
                Deactivated
              </Button>
            ) : (
              <>
                <Button
                  colorScheme="success"
                  type="button"
                  onClick={handleApprove}
                >
                  Approve User
                </Button>

                <Button type="button" onClick={handleBlockUser}>
                  Block User
                </Button>

                <Button
                  colorScheme="error"
                  type="button"
                  onClick={handleDomainBlockUser}
                >
                  Block Domain and User
                </Button>
              </>
            )}
          </ApprovalInformationSection>
        </>
      )}
    </CommonWrapper>
  );
};

export default UserApproval;
